import { useCallback, useMemo, useRef, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import {
  APPLY_CHUNK_SIZE,
  type ApplyRowResult,
} from "../../../api/officeIdentityBackfill.service.js";
import {
  parseRollback,
  toRestoreRow,
  type ParseRollbackResult,
  type RollbackRow,
} from "./parseRollback.js";
import { ResultsTable, STATUS_LABELS, countByStatus } from "./ResultsTable.js";
import { ROLLBACK_HEADERS } from "./backfillCsv.js";
import { runChunkedApply } from "./runApply.js";
import styles from "./OfficeIdentityBackfill.module.css";

/**
 * The undo half of the office identity backfill tool (DEV-2803): upload or
 * paste a rollback CSV the tool produced earlier, preview exactly what it will
 * restore, and apply it with mode "restore".
 *
 * The parse is strict — any malformed line blocks the whole file rather than
 * being skipped, because a rollback that silently applied only some of its
 * rows would leave the operator believing the run was fully undone. An empty
 * `previous` cell is meaningful: restore clears that field.
 */

type Phase = "idle" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

type PreviewRow = {
  id: string;
  officeId: string;
  field: string;
  previous: string;
};

const PREVIEW_COLUMNS: Column<PreviewRow>[] = [
  {
    header: "Office id",
    accessor: "officeId",
    render: (_value, row) => (
      <span className={styles.mono} title={row.officeId}>
        {row.officeId.slice(0, 8)}
      </span>
    ),
  },
  {
    header: "Field",
    accessor: "field",
    render: (_value, row) => <span className={styles.mono}>{row.field}</span>,
  },
  {
    header: "Restore to",
    accessor: "previous",
    wrap: true,
    render: (_value, row) =>
      row.previous === "" ? (
        <span className={`${styles.pill} ${styles.pillWarning}`}>clear the field</span>
      ) : (
        <span className={styles.mono}>{row.previous}</span>
      ),
  },
];

function toPreviewRow(row: RollbackRow): PreviewRow {
  return {
    // Line numbers are unique per parsed row, whatever the file repeats.
    id: `${row.lineNumber}`,
    officeId: row.officeId,
    field: row.field,
    previous: row.previous,
  };
}

export function RollbackSection() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseRollbackResult | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [isConfirming, setIsConfirming] = useState(false);
  const [progress, setProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  const [results, setResults] = useState<ApplyRowResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Parses whatever is in the box. A fresh preview discards any earlier run. */
  const preview = useCallback((csv: string) => {
    setResults(null);
    setError(null);
    setProgress({ done: 0, total: 0 });
    setPhase("idle");
    setParsed(parseRollback(csv));
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      let csv: string;
      try {
        csv = await file.text();
      } catch (readError) {
        setError(readError instanceof Error ? readError.message : String(readError));
        return;
      }
      setFileName(file.name);
      setText(csv);
      preview(csv);
    },
    [preview],
  );

  const applyRestore = useCallback(async () => {
    if (!parsed) {
      return;
    }
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results: runResults, failure } = await runChunkedApply(
      parsed.rows.map(toRestoreRow),
      "restore",
      (done, total) => setProgress({ done, total }),
    );

    setResults(runResults);
    setError(failure);
    setPhase("done");
  }, [parsed]);

  const previewRows = useMemo(() => (parsed ? parsed.rows.map(toPreviewRow) : []), [parsed]);

  const rowCount = parsed?.rows.length ?? 0;
  const canApply = parsed !== null && parsed.errors.length === 0 && rowCount > 0;

  const resultCounts = useMemo(() => countByStatus(results ?? []), [results]);
  const restoredCount = resultCounts("applied");

  const isBusy = phase === "applying";

  return (
    <div className={styles.stack}>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Rollback — restore previous values</div>
        <div className={styles.stack}>
          <div className={styles.rowNote}>
            Upload or paste a rollback CSV produced by this tool. Columns:{" "}
            <code>{ROLLBACK_HEADERS.join(",")}</code>. An empty <code>previous</code> cell clears
            the field.
          </div>
          <textarea
            className={styles.pasteBox}
            aria-label="Rollback CSV"
            placeholder={`${ROLLBACK_HEADERS.join(",")}\n…`}
            value={text}
            disabled={isBusy}
            onChange={event => {
              setText(event.target.value);
              setFileName(null);
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Rollback CSV file"
            accept=".csv,.tsv,.txt,text/csv"
            className={styles.hiddenInput}
            disabled={isBusy}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Clear the input's value, otherwise picking the SAME file again
              // fires no change event and appears to do nothing.
              event.target.value = "";
              if (chosen && !isBusy) {
                void loadFile(chosen);
              }
            }}
          />
          <div className={styles.toolbar}>
            <Button
              variant="outlined"
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse
            </Button>
            {fileName ? <span className={styles.rowNote}>{fileName}</span> : null}
            <span className={styles.spacer} />
            <Button
              variant="outlined"
              disabled={isBusy || text.trim() === ""}
              onClick={() => preview(text)}
            >
              Preview rollback
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      {parsed && parsed.errors.length > 0 ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          <div>
            {parsed.errors.length} problem{parsed.errors.length === 1 ? "" : "s"} in that file —
            nothing will be restored until they are fixed.
          </div>
          <ul className={styles.errorList}>
            {parsed.errors.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {parsed && parsed.errors.length === 0 && phase === "idle" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Rollback preview</div>
          <div className={styles.stack}>
            <DataTable data={previewRows} columns={PREVIEW_COLUMNS} emptyMessage="No rows." />
            <div className={styles.toolbar}>
              <span className={styles.resultCount}>
                {rowCount} row{rowCount === 1 ? "" : "s"} to restore
              </span>
              <span className={styles.spacer} />
              <Button
                variant="contained"
                disabled={!canApply}
                onClick={() => setIsConfirming(true)}
              >
                Apply restore
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Restoring…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${progress.done.toLocaleString()} / ${progress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            Sent in batches of {APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open.
          </div>
        </div>
      ) : null}

      {phase === "done" && results ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Restore result</div>
          <div className={styles.stack}>
            <span className={styles.resultCount}>
              {restoredCount.toLocaleString()} restored
              {(["noop", "skipped_not_empty", "invalid", "error"] as const)
                .filter(status => resultCounts(status) > 0)
                .map(status => ` · ${resultCounts(status)} ${STATUS_LABELS[status].toLowerCase()}`)
                .join("")}
            </span>
            <ResultsTable results={results} />
          </div>
        </div>
      ) : null}

      {isConfirming && parsed ? (
        <ConfirmationModal
          title="Restore office identity values"
          message={`${rowCount} office field value${rowCount === 1 ? "" : "s"} will be restored.`}
          warningMessage={
            "Restore writes the CSV's previous values over whatever the database " +
            "holds now — an empty previous cell clears the field."
          }
          confirmButtonText={`Restore ${rowCount} value${rowCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about hundreds of office rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyRestore}
        />
      ) : null}
    </div>
  );
}
